import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AngularPlugin } from "@microsoft/applicationinsights-angularplugin-js";
import {
  ApplicationInsights,
  ITelemetryItem,
} from "@microsoft/applicationinsights-web";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { map, tap } from "rxjs/operators";
import {
  CLSMetricWithAttribution,
  INPMetricWithAttribution,
  LCPMetricWithAttribution,
  onCLS,
  onINP,
  onLCP,
} from "web-vitals/attribution";

import { environment } from "../../../../environments/environment";
import * as TelemetryActions from "./telemetry.actions";

const CLOUD_ROLE = "portal-ui";
const SERVICE_NAMESPACE = "Portal";
const TELEMETRY_SCHEMA_VERSION = "otel-only";

@Injectable()
export class TelemetryEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);

  private appInsights: ApplicationInsights;
  private webVitalsInitialized = false;

  constructor() {
    const angularPlugin = new AngularPlugin();

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: environment.appInsightsConnectionString,
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router },
        },
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableFetchTracking: false,
        enableUnhandledPromiseRejectionTracking: true,
      },
    });

    this.registerResourceAttributes();
  }

  /**
   * Registers a telemetry initializer that attaches Tier 1 resource attributes
   * (cloud role, service identity) to every telemetry item emitted by this SPA.
   * Required by the "Standardize Telemetry" ADR so Portal.UI appears as a
   * distinct node in Application Map and correlates with backend services.
   */
  private registerResourceAttributes(): void {
    const roleInstance =
      typeof window !== "undefined" && window.location
        ? window.location.hostname || CLOUD_ROLE
        : CLOUD_ROLE;

    this.appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
      const tags = (item.tags = item.tags || {}) as Record<string, string>;
      tags["ai.cloud.role"] = CLOUD_ROLE;
      tags["ai.cloud.roleInstance"] = roleInstance;

      item.data = item.data || {};
      item.data["service.name"] = CLOUD_ROLE;
      item.data["service.namespace"] = SERVICE_NAMESPACE;
      item.data["deployment.environment"] = environment.environmentName;
    });
  }

  loadAppInsights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TelemetryActions.loadAppInsights),
      tap(() => this.appInsights.loadAppInsights()),
      // After App Insights loads, initialize Web Vitals tracking
      map(() => TelemetryActions.initializeWebVitals())
    )
  );

  logPageView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TelemetryActions.logPageView),
        tap(({ name, url }) =>
          this.appInsights.trackPageView({ name, uri: url })
        )
      ),
    { dispatch: false }
  );

  logEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TelemetryActions.logEvent),
        tap(({ name, properties }) =>
          this.appInsights.trackEvent({ name }, properties)
        )
      ),
    { dispatch: false }
  );

  logMetric$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TelemetryActions.logMetric),
        tap(({ name, average, properties }) =>
          this.appInsights.trackMetric({ name, average }, properties)
        )
      ),
    { dispatch: false }
  );

  logException$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TelemetryActions.logException),
        tap(({ exception, severityLevel }) =>
          this.appInsights.trackException({ exception, severityLevel })
        )
      ),
    { dispatch: false }
  );

  logTrace$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TelemetryActions.logTrace),
        tap(({ message, properties }) =>
          this.appInsights.trackTrace({ message }, properties)
        )
      ),
    { dispatch: false }
  );

  initializeWebVitals$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TelemetryActions.initializeWebVitals),
        tap(() => this.setupWebVitals())
      ),
    { dispatch: false }
  );

  /**
   * Sets up Core Web Vitals tracking using Google's web-vitals library.
   * Tracks LCP (Largest Contentful Paint), INP (Interaction to Next Paint), and CLS (Cumulative Layout Shift).
   *
   * These metrics provide industry-standard measurements of:
   * - Loading performance (LCP): Good < 2.5s, Poor > 4.0s
   * - Interactivity (INP): Good < 200ms, Poor > 500ms
   * - Visual stability (CLS): Good < 0.1, Poor > 0.25
   *
   * Phase 1 of the "Standardize Service Telemetry" ADR (ADO 58357): dual-wrote
   * legacy (`Frontend_*`) and OTel-namespaced (`du.frontend.*`) metrics and
   * attributes while native Application Insights RUM was validated in parallel.
   *
   * Phase 2: confirmed native Application Insights RUM does not collect Core
   * Web Vitals (LCP/INP/CLS) in this SPA — only PageViewPerformance/page-load
   * timing is available natively. Decision: this manual `web-vitals`
   * collection remains the long-term source of Web Vitals; dashboards
   * standardize on `du.frontend.*` naming.
   *
   * Phase 3 (this change): removed the legacy `Frontend_*` dual-write. Only
   * `du.frontend.*`-namespaced metrics are emitted now
   * (`_telemetry_schema_version` = "otel-only"). The manual `onLCP`/`onINP`/
   * `onCLS` observers and the `web-vitals` dependency are retained
   * intentionally — do not remove them; RUM has no substitute for these
   * metrics here.
   */
  private setupWebVitals(): void {
    if (this.webVitalsInitialized) {
      return;
    }
    this.webVitalsInitialized = true;

    // LCP - Largest Contentful Paint (loading performance)
    onLCP((metric: LCPMetricWithAttribution) => {
      const url = metric.attribution.navigationEntry?.name
        ? new URL(metric.attribution.navigationEntry.name).pathname
        : window.location.pathname;

      const properties = {
        "du.frontend.url": url,
        "du.frontend.rating": metric.rating,
        "du.frontend.delta": metric.delta,
        "du.frontend.navigation_type": metric.navigationType,
        _telemetry_schema_version: TELEMETRY_SCHEMA_VERSION,
      };

      this.appInsights.trackMetric(
        {
          name: `du.frontend.${metric.name.toLowerCase()}`,
          average: metric.value,
        },
        properties
      );
    });

    // INP - Interaction to Next Paint (responsiveness)
    onINP((metric: INPMetricWithAttribution) => {
      const properties = {
        "du.frontend.rating": metric.rating,
        "du.frontend.delta": metric.delta,
        "du.frontend.navigation_type": metric.navigationType,
        "du.frontend.interaction_target": metric.attribution.interactionTarget,
        _telemetry_schema_version: TELEMETRY_SCHEMA_VERSION,
      };

      this.appInsights.trackMetric(
        {
          name: `du.frontend.${metric.name.toLowerCase()}`,
          average: metric.value,
        },
        properties
      );
    });

    // CLS - Cumulative Layout Shift (visual stability)
    onCLS((metric: CLSMetricWithAttribution) => {
      const properties = {
        "du.frontend.rating": metric.rating,
        "du.frontend.delta": metric.delta,
        "du.frontend.navigation_type": metric.navigationType,
        "du.frontend.largest_shift_target":
          metric.attribution.largestShiftTarget,
        _telemetry_schema_version: TELEMETRY_SCHEMA_VERSION,
      };

      this.appInsights.trackMetric(
        {
          name: `du.frontend.${metric.name.toLowerCase()}`,
          average: metric.value,
        },
        properties
      );
    });
  }
}
